/* eslint-disable max-lines */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FacetOption } from './types';

function getAppliedFilterLabel({
    totalResults,
    unfilteredTotalResults,
    entitySingular,
    entityPlural,
}: {
    totalResults: number | null;
    unfilteredTotalResults: number | null;
    entitySingular: string;
    entityPlural: string;
}) {
    let totalResultsLabel = '';

    if (totalResults === null || totalResults <= 0) {
        totalResultsLabel = `No ${entityPlural} found`;
    } else if (unfilteredTotalResults === totalResults && totalResults === 1) {
        totalResultsLabel = `Showing ${totalResults} ${entitySingular}`;
    } else if (unfilteredTotalResults === totalResults) {
        totalResultsLabel = `Showing ${totalResults.toLocaleString()} ${entityPlural}`;
    } else if (unfilteredTotalResults === 1) {
        totalResultsLabel = `Showing ${totalResults} of ${unfilteredTotalResults} ${entitySingular}`;
    } else {
        totalResultsLabel = `Showing ${totalResults.toLocaleString()} of ${unfilteredTotalResults?.toLocaleString()} ${entityPlural}`;
    }

    return totalResultsLabel;
}

function useAppliedFilterLabel({
    totalResults,
    unfilteredTotalResults,
    entitySingular,
    entityPlural,
}: {
    totalResults: number | null;
    unfilteredTotalResults: number | null;
    entitySingular: string;
    entityPlural: string;
}) {
    return useMemo(() => getAppliedFilterLabel({
        totalResults,
        unfilteredTotalResults,
        entitySingular,
        entityPlural,
    }), [totalResults, unfilteredTotalResults, entitySingular, entityPlural]);
}

function useFacetFilter(facetOptions: FacetOption[] = [], {
    onChange = () => {},
}: {
    onChange?: () => void;
} = {}) {
    const [searchParams, setSearchParams] = useSearchParams();

    const setFacet = (facet: string, isEnabled: boolean) => {
        const isValidFacet = facetOptions.some((option) => option.value === facet);

        if (!isValidFacet) return;

        setSearchParams(
            (params) => {
                const facets = params.getAll('facet');

                if (isEnabled && facets.includes(facet)) return params;
                if (!isEnabled && !facets.includes(facet)) return params;

                if (isEnabled) {
                    params.append('facet', facet);
                } else {
                    params.delete('facet', facet);
                }

                return params;
            },
            {
                replace: true,
            }
        );
        onChange();
    };

    const facets = useMemo(() => searchParams.getAll('facet'), [searchParams]);

    const clearFacets = () => {
        facetOptions.forEach((option) => setFacet(option.value, false));
    };

    const areAllChecked = (category: string) => facetOptions
        .filter((option) => option.category === category)
        .every((option) => facets.includes(option.value));

    const areSomeChecked = (category: string) => facetOptions
        .filter((option) => option.category === category)
        .some((option) => facets.includes(option.value));

    const setMultipleFacets = (category: string, isEnabled: boolean) => {
        facetOptions
            .filter((option) => option.category === category)
            .forEach((option) => {
                setFacet(option.value, isEnabled);
            });
    };

    return {
        facets,
        getAppliedFilterLabel,
        setFacet,
        clearFacets,
        setMultipleFacets,
        areAllChecked,
        areSomeChecked,
    };
}

export { useFacetFilter, useAppliedFilterLabel };
